// The browser client for an App's per-run and monthly budget policy (B3). Unavailable until the
// server has an actual writer/reader: a read that is not exactly shaped is "unavailable", never a
// default, and nothing here reads a limit from interview text or usage aggregates. Limits are whole
// credits (D74) inside the bounds the server states; no amount of money travels either way.
export type CreditBounds = { min: number; max: number };
export type BudgetLimits = { perRun: CreditBounds; monthly: CreditBounds };
export type BudgetView = {
  state: 'set' | 'unset';
  perRunCredits: number | null;
  monthlyCredits: number | null;
  policyVersion: string | null;
  limits: BudgetLimits;
};
export type BudgetCommand = {
  operationId: string;
  /** The version the person saw; null when none was set. */
  expectedPolicyVersion: string | null;
  perRunCredits: number;
  monthlyCredits: number;
};
export type BudgetOutcome =
  | { kind: 'ok'; view: BudgetView }
  | { kind: 'refused'; reason: 'not_authorized' | 'invalid' | 'changed' }
  | { kind: 'unknown' };

const PREFIX = '/api/v1/company/apps/budget';
const WHOLE = /^\d{1,9}$/u;

/** "150" -> 150; null for anything that is not a plain positive whole number of credits. */
export function creditsFromText(text: string): number | null {
  if (!WHOLE.test(text)) return null;
  const value = Number(text);
  return value > 0 ? value : null;
}

/**
 * The saved limits as the ScheduleForm's whole-credit text, or null while none is set (an unset
 * policy shows empty fields, never a default). `ScheduleDraft.perRunLimit/monthlyLimit` take these.
 */
export function limitsFromView(
  view: BudgetView | null,
): { perRunLimit: string; monthlyLimit: string } | null {
  if (!view || view.state !== 'set' || view.perRunCredits === null || view.monthlyCredits === null)
    return null;
  return { perRunLimit: String(view.perRunCredits), monthlyLimit: String(view.monthlyCredits) };
}

const obj = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const whole = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
function parseBounds(value: unknown): CreditBounds | null {
  const raw = obj(value);
  return raw && whole(raw.min) && whole(raw.max) && raw.min <= raw.max
    ? { min: raw.min, max: raw.max }
    : null;
}
const within = (value: unknown, bounds: CreditBounds) =>
  value === null || (whole(value) && value >= bounds.min && value <= bounds.max);

export function parseBudgetView(value: unknown): BudgetView | null {
  const raw = obj(value);
  if (!raw || (raw.state !== 'set' && raw.state !== 'unset')) return null;
  const limits = obj(raw.limits);
  const perRun = parseBounds(limits?.perRun);
  const monthly = parseBounds(limits?.monthly);
  if (!perRun || !monthly) return null;
  if (!within(raw.perRunCredits, perRun) || !within(raw.monthlyCredits, monthly)) return null;
  if (raw.policyVersion !== null && typeof raw.policyVersion !== 'string') return null;
  // A set policy states both limits; an unset one states none.
  const set = raw.state === 'set';
  if (set !== (raw.perRunCredits !== null && raw.monthlyCredits !== null)) return null;
  if (!set && (raw.perRunCredits !== null || raw.monthlyCredits !== null)) return null;
  return {
    state: raw.state,
    perRunCredits: raw.perRunCredits as number | null,
    monthlyCredits: raw.monthlyCredits as number | null,
    policyVersion: raw.policyVersion as string | null,
    limits: { perRun, monthly },
  };
}

/**
 * The server refused the READ because the person is not the company owner: the producer's explicit code
 * `budget_management_required`, and only that. Any other refusal (lost membership, session, disclosure,
 * an App this person cannot read) is NOT an owner rule and stays "unavailable".
 */
export class BudgetOwnerOnly extends Error {
  constructor() {
    super('budget_owner_only');
    this.name = 'BudgetOwnerOnly';
  }
}
export const BUDGET_OWNER_ONLY_CODE = 'budget_management_required';

export interface AppBudgetApi {
  read(): Promise<BudgetView>;
  write(command: BudgetCommand): Promise<BudgetOutcome>;
}

/** For the named selected version of the saved App (artifact + version), never a logical id alone. */
export function createAppBudgetApi(
  artifactId: string,
  versionId: string,
  send: typeof fetch,
): AppBudgetApi {
  const url = `${PREFIX}/${encodeURIComponent(artifactId)}/${encodeURIComponent(versionId)}`;
  const parsed = async (response: Response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };
  return {
    async read() {
      const response = await send(url, { credentials: 'include' });
      if (response.status === 403) {
        const refusal = obj(await parsed(response));
        if (refusal?.error === BUDGET_OWNER_ONLY_CODE) throw new BudgetOwnerOnly();
        throw new Error('budget_unavailable');
      }
      const view = response.status === 200 ? parseBudgetView(await parsed(response)) : null;
      if (!view) throw new Error('budget_unavailable');
      return view;
    },
    async write(command) {
      let response: Response;
      try {
        response = await send(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(command),
        });
      } catch {
        return { kind: 'unknown' };
      }
      if (response.status === 200) {
        const view = parseBudgetView(await parsed(response));
        return view ? { kind: 'ok', view } : { kind: 'unknown' };
      }
      if (response.status === 403) return { kind: 'refused', reason: 'not_authorized' };
      if (response.status === 409) return { kind: 'refused', reason: 'changed' };
      if (response.status === 400 || response.status === 422)
        return { kind: 'refused', reason: 'invalid' };
      return { kind: 'unknown' };
    },
  };
}
